import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CloseIcon from "@mui/icons-material/Close";
// import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import AddressForm from "../../../../ui/utils/AddressForm";
import CustomDatePicker from "../../../../ui/utils/CustomDatePicker";
import {
  printSampleShipmentDetail,
  printSampleShipmentSamples,
} from "../../../../ui/SamplePdfGenerator";
import { store } from "../../../../../store";
import {
  editSampleShipment,
  getSampleShipment,
  getSampleShipmentByReference,
} from "../../../../../redux/actions/sampleShipment";
import useShipmentCenter from "./shared/useShipmentCenter";
import { getSamples } from "../../../../../redux/actions/sample";
import { getStudyInfo } from "../../../../../redux/actions/study";
import { showErrorAlert } from "../../../../ui/utils/AlertUtils";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../shared/ui/toast/Toast";
import SampleSelectionSection, {
  sampleTypeLabel,
} from "./shared/SampleSelectionSection";
import PageHeader from "../../../../ui/PageHeader";
import StudyCodeLabelHelper from "./shared/StudyCodeLabelHelper";
import SampleShipmentAudit from "./shared/SampleShipmentAudit";
import SampleShipmentSteps from "./shared/SampleShipmentSteps";
import { cdlOperator } from "../../../../../shared/AccessHelper";
import { getSampleShipmentDestinations } from "../../../../../services/sampleSetupService";

const sectionSx = {
  p: { xs: 2, md: 3 },
  borderRadius: 2,
  borderColor: "divider",
};

// const airwaybillDocumentActionsEnabled = false;
const BOX_TYPE_OPTIONS = ["QS1", "QS2", "QS3", "Credo 10L"];
const normalizeBoxTypes = (value) =>
  (Array.isArray(value) ? value : (value ?? "").split(","))
    .map((boxType) => boxType.trim())
    .filter(Boolean);

const EMPTY_DESTINATION_ADDRESS = Object.freeze({});
const DESTINATION_ADDRESS_FIELDS = [
  "hospital",
  "ward",
  "building",
  "street",
  "zone",
  "floor",
  "zipcode",
  "city",
  "country",
  "contactName",
  "contactPhone",
  "contactEmail",
];
const MEANINGFUL_DESTINATION_ADDRESS_FIELDS = DESTINATION_ADDRESS_FIELDS.filter(
  (field) => field !== "city" && field !== "country",
);

const normalizeDestinationValue = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const isLegacyDefaultDestination = (value) =>
  normalizeDestinationValue(value) === "vela dx";

const hasMeaningfulDestination = (address, textValue) => {
  if (textValue && !isLegacyDefaultDestination(textValue)) return true;

  return MEANINGFUL_DESTINATION_ADDRESS_FIELDS.some((field) => {
    const value = address?.[field];
    return (
      Boolean(value) &&
      !(field === "hospital" && isLegacyDefaultDestination(value))
    );
  });
};

const destinationMatchesAddress = (destination, address) =>
  DESTINATION_ADDRESS_FIELDS.every(
    (field) =>
      String(destination?.[field] ?? "").trim() ===
      String(address?.[field] ?? "").trim(),
  );

const combineDateAndTime = (dateValue, timeValue) => {
  const date = dayjs(dateValue);
  if (!date.isValid()) return null;

  if (typeof timeValue === "string" && /^\d{1,2}:\d{2}/.test(timeValue)) {
    const [hours, minutes] = timeValue.split(":");
    return date
      .hour(Number(hours))
      .minute(Number(minutes))
      .second(0)
      .millisecond(0)
      .toISOString();
  }

  const time = dayjs.isDayjs(timeValue) ? timeValue : dayjs(timeValue);
  if (!time.isValid()) return date.toISOString();

  return date
    .hour(time.hour())
    .minute(time.minute())
    .second(0)
    .millisecond(0)
    .toISOString();
};

const timeStringFromValue = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    const timeMatch = value.match(/^(\d{1,2}):(\d{2})/);
    if (timeMatch) {
      return `${timeMatch[1].padStart(2, "0")}:${timeMatch[2]}`;
    }
  }

  const parsed = dayjs.isDayjs(value) ? value : dayjs(value);
  return parsed.isValid() ? parsed.format("HH:mm") : "";
};

const minutesFromTimeValue = (value) => {
  const timeString = timeStringFromValue(value);
  const timeMatch = timeString.match(/^(\d{2}):(\d{2})$/);
  if (!timeMatch) return null;

  return Number(timeMatch[1]) * 60 + Number(timeMatch[2]);
};

const addMinutesToTimeValue = (value, minutes) => {
  const startMinutes = minutesFromTimeValue(value);
  if (startMinutes === null) return "";

  const nextMinutes = (startMinutes + minutes) % (24 * 60);
  const hours = Math.floor(nextMinutes / 60);
  const mins = nextMinutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
};

const asTimePickerValue = (value) => {
  const timeString = timeStringFromValue(value);
  const timeMatch = timeString.match(/^(\d{2}):(\d{2})$/);
  if (!timeMatch) return null;

  return dayjs()
    .hour(Number(timeMatch[1]))
    .minute(Number(timeMatch[2]))
    .second(0)
    .millisecond(0);
};

const normalizeDraftValue = (value) =>
  value === undefined || value === null ? "" : String(value);

const normalizeDraftDateTime = (value) => {
  const date = dayjs(value);
  return date.isValid() ? date.second(0).millisecond(0).toISOString() : "";
};

const normalizeDraftSampleIds = (samplesOrIds = []) =>
  samplesOrIds
    .map((sampleOrId) => normalizeDraftValue(sampleOrId?.id ?? sampleOrId))
    .filter(Boolean)
    .sort();

const normalizeDraftAddress = (address = {}) => ({
  hospital: normalizeDraftValue(address.hospital),
  ward: normalizeDraftValue(address.ward),
  building: normalizeDraftValue(address.building),
  street: normalizeDraftValue(address.street),
  floor: normalizeDraftValue(address.floor),
  zone: normalizeDraftValue(address.zone),
  zipcode: normalizeDraftValue(address.zipcode),
  city: normalizeDraftValue(address.city),
  country: normalizeDraftValue(address.country),
  contactName: normalizeDraftValue(address.contactName),
  contactPhone: normalizeDraftValue(address.contactPhone),
  contactEmail: normalizeDraftValue(address.contactEmail),
  centerNumber: normalizeDraftValue(address.centerNumber),
});

const normalizeDraftDate = (value) => normalizeDraftDateTime(value);

const normalizeDraftBoolean = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    return ["true", "1", "yes", "on"].includes(value.trim().toLowerCase());
  }
  return Boolean(value);
};

const normalizeTrackingContact = (tracking = {}) => ({
  name: normalizeDraftValue(tracking.name),
  email: normalizeDraftValue(tracking.email),
  date: normalizeDraftDate(tracking.date),
});

const buildComparableShipmentDraft = (draft) => {
  if (!draft) return null;

  return {
    centerId: normalizeDraftValue(draft.centerId),
    pickupAddress: normalizeDraftAddress(draft.pickupAddress),
    destinationAddress: normalizeDraftAddress(draft.destinationAddress),
    pickupDatetimeStart: normalizeDraftDateTime(draft.pickupDatetimeStart),
    pickupDatetimeEnd: normalizeDraftDateTime(draft.pickupDatetimeEnd),
    carrier: normalizeDraftValue(draft.carrier),
    airwaybill: normalizeDraftValue(draft.airwaybill || draft.trackingNumber),
    numberOfPackages: normalizeDraftValue(draft.numberOfPackages),
    boxType: normalizeBoxTypes(draft.boxType).sort().join(", "),
    temperatureProbe: normalizeDraftBoolean(draft.temperatureProbe),
    sampleIds: normalizeDraftSampleIds(draft.sampleIds || draft.samples),
    requestTracking: normalizeTrackingContact(draft.requestTracking),
    confirmTracking: normalizeTrackingContact(draft.confirmTracking),
    receiveTracking: normalizeTrackingContact(draft.receiveTracking),
  };
};

const areShipmentDraftsEqual = (left, right) =>
  JSON.stringify(left) === JSON.stringify(right);

const CheckboxField = ({ label, checked, disabled, onChange }) => (
  <Box
    sx={{
      minHeight: 56,
      px: 0.5,
      display: "flex",
      alignItems: "center",
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1,
      bgcolor: "background.paper",
    }}>
    <FormControlLabel
      sx={{ m: 0 }}
      control={
        <Checkbox
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
        />
      }
      label={label}
    />
  </Box>
);

const groupSamplesByPatient = (samples) => {
  const grouped = new Map();

  samples.forEach((sample) => {
    const patientKey =
      sample.patientId ||
      sample.patient?.id ||
      sample.patientCode ||
      sample.patientKey ||
      "unknown";
    const label =
      sample.patientCode ||
      sample.patientKey ||
      sample.patient?.code ||
      sample.patient?.number ||
      String(patientKey);

    if (!grouped.has(patientKey)) {
      grouped.set(patientKey, {
        selectedPatient: label,
        patientId: sample.patientId || sample.patient?.id || null,
        samples: [],
      });
    }

    const typeLabel = sampleTypeLabel(sample);

    grouped.get(patientKey).samples.push({
      selectedAnalysis: sample.analyses?.[0]?.id || null,
      selectedVisits: (sample.visits || []).map((visit) => visit.id),
      totalSamples: sample.quantity || 1,
      sampleType: typeLabel === "-" ? "" : typeLabel,
    });
  });

  return Array.from(grouped.values());
};

const SectionHeader = ({ title, description, action }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: { xs: "flex-start", sm: "center" },
      justifyContent: "space-between",
      flexDirection: { xs: "column", sm: "row" },
      gap: 1.5,
      mb: 2.5,
    }}>
    <Box>
      <Typography
        variant="h6"
        sx={{ color: "primary.main", fontWeight: 700, lineHeight: 1.25 }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {description}
        </Typography>
      )}
    </Box>
    {action}
  </Box>
);

const DetailField = ({ label, value }) => (
  <Grid item xs={12} sm={6} md={4}>
    <TextField
      fullWidth
      label={label}
      value={value || "-"}
      InputProps={{ readOnly: true }}
      InputLabelProps={{ shrink: true }}
    />
  </Grid>
);

const formatTrackingUserName = (user) =>
  [user?.firstname || user?.firstName, user?.name || user?.lastName]
    .filter(Boolean)
    .join(" ") ||
  user?.username ||
  user?.email ||
  "";

export const ScreenSampleShipmentDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { reference } = useParams();
  const { t } = useTranslation();
  const sampleShipment = useSelector(
    (state) => state.sampleShipment.sampleShipment || {},
  );
  const samples = useSelector((state) => state.sample.samples) || [];
  const center = useShipmentCenter(
    sampleShipment.centerId || location.state?.shipment?.centerId,
  );
  const study = useSelector((state) => state.study.study);
  const currentUser = useSelector((state) => state.user.user);
  const sampleShipmentId = location.state?.sampleShipmentId;
  const isRequestedShipmentLoaded = sampleShipmentId
    ? String(sampleShipment.id) === String(sampleShipmentId)
    : reference
      ? sampleShipment.reference === reference
      : Boolean(sampleShipment.id);
  const canViewDestinationAddress = cdlOperator(currentUser);
  const normalizedShipmentStatus = normalizeDraftValue(sampleShipment.status)
    .trim()
    .toLowerCase();
  const isDraftShipment = normalizedShipmentStatus === "draft";
  const canEditShipmentInfo =
    isRequestedShipmentLoaded &&
    (isDraftShipment || canViewDestinationAddress);
  const [defaultConfirmTrackingDate] = React.useState(() =>
    new Date().toISOString(),
  );
  const currentUserConfirmTracking = React.useMemo(
    () => ({
      name: formatTrackingUserName(currentUser),
      email: currentUser?.email || "",
      date: defaultConfirmTrackingDate,
    }),
    [currentUser, defaultConfirmTrackingDate],
  );
  const draftShipment = location.state?.shipment || {};
  const shipmentCenterId =
    sampleShipment.centerId || draftShipment.centerId || center?.id;
  const destinationStudyId =
    sampleShipment.center?.studyId ||
    (String(center?.id) === String(shipmentCenterId)
      ? center?.studyId
      : null) ||
    location.state?.studyId;
  const cdlStudyCode =
    study?.cdlStudyCode ||
    sampleShipment.studyCode ||
    location.state?.studyCode ||
    center?.Study?.cdlStudyCode ||
    "-";

  const pickupAddress =
    sampleShipment.pickupAddress ||
    sampleShipment.address ||
    draftShipment.address ||
    {};
  const draftDestination =
    draftShipment.address?.destination || EMPTY_DESTINATION_ADDRESS;
  const rawDestinationAddress = sampleShipment.destinationAddress;
  const storedDestinationAddress =
    rawDestinationAddress && typeof rawDestinationAddress === "object"
      ? rawDestinationAddress
      : draftDestination;
  const storedDestinationText =
    typeof rawDestinationAddress === "string"
      ? rawDestinationAddress
      : "";
  const hasSavedDestination = hasMeaningfulDestination(
    storedDestinationAddress,
    storedDestinationText,
  );
  const destinationAddress = hasSavedDestination
    ? storedDestinationAddress
    : EMPTY_DESTINATION_ADDRESS;
  const destinationText =
    hasSavedDestination && !isLegacyDefaultDestination(storedDestinationText)
      ? storedDestinationText
      : "";
  const requester =
    sampleShipment.user ||
    sampleShipment.createdByUser ||
    draftShipment.user ||
    currentUser;
  const requestDateValue =
    sampleShipment.sendingDate ||
    sampleShipment.createdAt ||
    draftShipment.expectedShippingDate ||
    null;
  const [requestTrackingDate, setRequestTrackingDate] = React.useState(
    requestDateValue || new Date(),
  );
  const requestDate = requestTrackingDate || requestDateValue;
  const [confirmTracking, setConfirmTracking] = React.useState({
    name: "",
    email: "",
    date: "",
  });
  const [receiveTracking, setReceiveTracking] = React.useState({
    name: "",
    email: "",
    date: "",
  });
  const [hospital, setHospital] = React.useState("");
  const [ward, setWard] = React.useState("");
  const [building, setBuilding] = React.useState("");
  const [street, setStreet] = React.useState("");
  const [floor, setFloor] = React.useState("");
  const [zone, setZone] = React.useState("");
  const [zipcode, setZipcode] = React.useState("");
  const [contactName, setContactName] = React.useState("");
  const [contactPhone, setContactPhone] = React.useState("");
  const [contactEmail, setContactEmail] = React.useState("");
  const [destinationHospital, setDestinationHospital] = React.useState("");
  const [destinationWard, setDestinationWard] = React.useState("");
  const [destinationBuilding, setDestinationBuilding] = React.useState("");
  const [destinationStreet, setDestinationStreet] = React.useState("");
  const [destinationZone, setDestinationZone] = React.useState("");
  const [destinationFloor, setDestinationFloor] = React.useState("");
  const [destinationZipcode, setDestinationZipcode] = React.useState("");
  const [destinationCity, setDestinationCity] = React.useState("");
  const [destinationCountry, setDestinationCountry] = React.useState("");
  const [destinationContactName, setDestinationContactName] =
    React.useState("");
  const [destinationContactPhone, setDestinationContactPhone] =
    React.useState("");
  const [destinationContactEmail, setDestinationContactEmail] =
    React.useState("");
  const [destinationOptions, setDestinationOptions] = React.useState([]);
  const [selectedDestinationId, setSelectedDestinationId] =
    React.useState("");
  const [autoAppliedDestination, setAutoAppliedDestination] =
    React.useState(null);
  const [initializedShipmentSourceKey, setInitializedShipmentSourceKey] =
    React.useState(null);
  const fieldErrors = React.useMemo(() => ({}), []);

  const allSamples = React.useMemo(() => {
    if (sampleShipment.shipmentSamples?.length)
      return sampleShipment.shipmentSamples;
    if (draftShipment.samples?.length) return draftShipment.samples;

    return [];
  }, [draftShipment.samples, sampleShipment.shipmentSamples]);

  const [selectedSamples, setSelectedSamples] = React.useState([]);
  const [temperatureProbe, setTemperatureProbe] = React.useState(false);
  const [carrierNeedsAttention, setCarrierNeedsAttention] =
    React.useState(false);
  const carrierFieldRef = React.useRef(null);
  const carrierSelectRef = React.useRef(null);

  const [shipmentDataFrames, setShipmentDataFrames] = React.useState([
    {
      carrier: "",
      airwaybill: "",
      numberOfPackages: "",
      boxType: [],
      dateOfShipment: null,
      startTimeOfShipment: null,
      endTimeOfShipment: null,
      selectedFile: null,
      sampleIds: [],
    },
  ]);

  const applyDestination = React.useCallback((destination) => {
    setDestinationHospital(destination?.hospital || "");
    setDestinationWard(destination?.ward || "");
    setDestinationBuilding(destination?.building || "");
    setDestinationStreet(destination?.street || "");
    setDestinationZone(destination?.zone || "");
    setDestinationFloor(destination?.floor || "");
    setDestinationZipcode(destination?.zipcode || "");
    setDestinationCity(destination?.city || "");
    setDestinationCountry(destination?.country || "");
    setDestinationContactName(destination?.contactName || "");
    setDestinationContactPhone(destination?.contactPhone || "");
    setDestinationContactEmail(destination?.contactEmail || "");
  }, []);

  React.useEffect(() => {
    setSelectedSamples(allSamples);
  }, [allSamples]);

  React.useEffect(() => {
    setTemperatureProbe(normalizeDraftBoolean(sampleShipment.temperatureProbe));
  }, [sampleShipment.id, sampleShipment.temperatureProbe]);

  React.useEffect(() => {
    if (shipmentCenterId) {
      store.dispatch(getSamples());
    }
  }, [shipmentCenterId]);

  const editablePickupAddress = React.useMemo(
    () => ({
      ...pickupAddress,
      hospital,
      ward,
      building,
      street,
      floor,
      zone,
      zipcode,
      city: pickupAddress.city || center?.city || "",
      country: pickupAddress.country || center?.country || "",
      contactName,
      contactPhone,
      contactEmail,
      centerNumber:
        pickupAddress.centerNumber ||
        draftShipment.centerNumber ||
        center?.centerNumber ||
        "",
    }),
    [
      building,
      center?.centerNumber,
      center?.city,
      center?.country,
      contactEmail,
      contactName,
      contactPhone,
      draftShipment.centerNumber,
      floor,
      hospital,
      pickupAddress,
      street,
      ward,
      zipcode,
      zone,
    ],
  );

  const editableDestinationAddress = React.useMemo(
    () => ({
      ...destinationAddress,
      hospital: destinationHospital,
      ward: destinationWard,
      building: destinationBuilding,
      street: destinationStreet,
      zone: destinationZone,
      floor: destinationFloor,
      zipcode: destinationZipcode,
      city: destinationCity,
      country: destinationCountry,
      contactName: destinationContactName,
      contactPhone: destinationContactPhone,
      contactEmail: destinationContactEmail,
    }),
    [
      destinationAddress,
      destinationBuilding,
      destinationCity,
      destinationContactEmail,
      destinationContactName,
      destinationContactPhone,
      destinationCountry,
      destinationFloor,
      destinationHospital,
      destinationStreet,
      destinationWard,
      destinationZipcode,
      destinationZone,
    ],
  );

  const printableShipment = React.useMemo(() => {
    const samplesById = new Map();

    [...samples, ...allSamples].forEach((sample) => {
      if (sample?.id !== undefined && sample?.id !== null) {
        samplesById.set(String(sample.id), sample);
      }
    });

    const printableSamples = selectedSamples.map((sample) => {
      const hydratedSample = samplesById.get(String(sample.id));

      if (!hydratedSample) {
        return sample;
      }

      return {
        ...hydratedSample,
        ...sample,
        analyses: sample.analyses?.length
          ? sample.analyses
          : hydratedSample.analyses || hydratedSample.Analyses || [],
        analysis:
          sample.analysis ||
          sample.Analysis ||
          hydratedSample.analysis ||
          hydratedSample.Analysis ||
          null,
      };
    });

    return {
      ...draftShipment,
      ...sampleShipment,
      address: editablePickupAddress,
      pickupAddress: editablePickupAddress,
      destinationAddress: editableDestinationAddress,
      samples: printableSamples,
      patients: groupSamplesByPatient(printableSamples),
      temperatureProbe,
      user: requester,
      User: requester,
    };
  }, [
    allSamples,
    editableDestinationAddress,
    editablePickupAddress,
    draftShipment,
    requester,
    sampleShipment,
    samples,
    selectedSamples,
    temperatureProbe,
  ]);

  React.useEffect(() => {
    if (sampleShipmentId) {
      store.dispatch(getSampleShipment(sampleShipmentId));
      return;
    }

    if (reference) {
      store.dispatch(getSampleShipmentByReference(reference));
    }
  }, [reference, sampleShipmentId]);

  React.useEffect(() => {
    if (center?.studyId) {
      store.dispatch(getStudyInfo(center.studyId));
    }
  }, [center]);

  React.useEffect(() => {
    let active = true;

    if (!canViewDestinationAddress || !destinationStudyId) {
      setDestinationOptions([]);
      setSelectedDestinationId("");
      setAutoAppliedDestination(null);
      return undefined;
    }

    getSampleShipmentDestinations(destinationStudyId)
      .then((destinations) => {
        if (!active) return;

        const nextDestinations = destinations || [];
        const selectedDestination = nextDestinations.find((destination) =>
          destinationMatchesAddress(destination, storedDestinationAddress),
        );
        const defaultDestination =
          selectedDestination ||
          (!hasSavedDestination ? nextDestinations[0] : null);

        setDestinationOptions(nextDestinations);
        setSelectedDestinationId(defaultDestination?.id || "");
        setAutoAppliedDestination(
          !hasSavedDestination ? defaultDestination || null : null,
        );

        if (!hasSavedDestination) {
          applyDestination(defaultDestination);
        }
      })
      .catch((error) => {
        if (!active) return;
        console.error("Failed to load sample destinations:", error);
        setDestinationOptions([]);
        setSelectedDestinationId("");
        setAutoAppliedDestination(null);
      });

    return () => {
      active = false;
    };
  }, [
    applyDestination,
    canViewDestinationAddress,
    destinationStudyId,
    hasSavedDestination,
    sampleShipment.id,
    storedDestinationAddress,
  ]);

  React.useEffect(() => {
    setHospital(pickupAddress.hospital || "");
    setWard(pickupAddress.ward || "");
    setBuilding(pickupAddress.building || "");
    setStreet(pickupAddress.street || "");
    setFloor(pickupAddress.floor || "");
    setZone(pickupAddress.zone || "");
    setZipcode(pickupAddress.zipcode || "");
    setContactName(pickupAddress.contactName || "");
    setContactPhone(pickupAddress.contactPhone || "");
    setContactEmail(pickupAddress.contactEmail || "");
    setRequestTrackingDate(requestDateValue || "");

    setDestinationHospital(
      destinationAddress.hospital || destinationText || "",
    );
    setDestinationWard(destinationAddress.ward || "");
    setDestinationBuilding(destinationAddress.building || "");
    setDestinationStreet(destinationAddress.street || "");
    setDestinationZone(destinationAddress.zone || "");
    setDestinationFloor(destinationAddress.floor || "");
    setDestinationZipcode(destinationAddress.zipcode || "");
    setDestinationCity(destinationAddress.city || "");
    setDestinationCountry(destinationAddress.country || "");
    setDestinationContactName(destinationAddress.contactName || "");
    setDestinationContactPhone(destinationAddress.contactPhone || "");
    setDestinationContactEmail(destinationAddress.contactEmail || "");
  }, [
    destinationAddress.building,
    destinationAddress.city,
    destinationAddress.contactEmail,
    destinationAddress.contactName,
    destinationAddress.contactPhone,
    destinationAddress.country,
    destinationAddress.floor,
    destinationAddress.hospital,
    destinationAddress.street,
    destinationAddress.ward,
    destinationAddress.zipcode,
    destinationAddress.zone,
    destinationText,
    requestDateValue,
    pickupAddress.building,
    pickupAddress.contactEmail,
    pickupAddress.contactName,
    pickupAddress.contactPhone,
    pickupAddress.floor,
    pickupAddress.hospital,
    pickupAddress.street,
    pickupAddress.ward,
    pickupAddress.zipcode,
    pickupAddress.zone,
  ]);

  React.useEffect(() => {
    const hasConfirmation =
      Boolean(sampleShipment.confirmedDatetime) ||
      Boolean(sampleShipment.confirmedByUser);
    const isShipmentRequested =
      String(sampleShipment.status || "")
        .trim()
        .toLowerCase() === "requested";

    setConfirmTracking(
      hasConfirmation
        ? {
            name: formatTrackingUserName(sampleShipment.confirmedByUser),
            email: sampleShipment.confirmedByUser?.email || "",
            date: sampleShipment.confirmedDatetime || "",
          }
        : isShipmentRequested
          ? currentUserConfirmTracking
          : {
              name: "",
              email: "",
              date: "",
            },
    );
    setReceiveTracking({
      name:
        formatTrackingUserName(sampleShipment.receiveByUser) ||
        formatTrackingUserName(sampleShipment.reception?.receiveByUser),
      email:
        sampleShipment.receiveByUser?.email ||
        sampleShipment.reception?.receiveByUser?.email ||
        "",
      date:
        sampleShipment.receiveDatetime ||
        sampleShipment.reception?.receiveDatetime ||
        "",
    });
  }, [
    sampleShipment.confirmedByUser,
    sampleShipment.confirmedDatetime,
    sampleShipment.status,
    sampleShipment.receiveByUser,
    sampleShipment.receiveDatetime,
    sampleShipment.reception,
    currentUserConfirmTracking,
  ]);

  const handleRequestTrackingChange = React.useCallback((nextTracking) => {
    setContactName(nextTracking.name || "");
    setContactEmail(nextTracking.email || "");
    setRequestTrackingDate(nextTracking.date || "");
  }, []);

  const handleDestinationChange = (destinationId) => {
    const destination = destinationOptions.find(
      (option) => String(option.id) === String(destinationId),
    );
    if (!destination) return;

    setSelectedDestinationId(destination.id);
    applyDestination(destination);
  };

  React.useEffect(() => {
    const dateOfShipment =
      sampleShipment.expectedShippingDate ||
      sampleShipment.pickupDatetimeStart ||
      draftShipment.expectedShippingDate ||
      null;
    const startTimeOfShipment =
      sampleShipment.pickupDatetimeStart || draftShipment.pickUpSampleStartTime;
    const endTimeOfShipment =
      sampleShipment.pickupDatetimeEnd || draftShipment.pickUpSampleEndTime;

    setShipmentDataFrames((currentFrames) => {
      const firstFrame = currentFrames[0] || {};
      return [
        {
          ...firstFrame,
          carrier: normalizeDraftValue(sampleShipment.carrier),
          airwaybill: normalizeDraftValue(
            sampleShipment.trackingNumber || sampleShipment.airwaybill,
          ),
          numberOfPackages: normalizeDraftValue(
            sampleShipment.numberOfPackages,
          ),
          boxType: normalizeBoxTypes(sampleShipment.boxType),
          dateOfShipment,
          startTimeOfShipment: timeStringFromValue(startTimeOfShipment),
          endTimeOfShipment: timeStringFromValue(endTimeOfShipment),
          sampleIds: allSamples.map((sample) => sample.id),
        },
      ];
    });
  }, [
    allSamples,
    draftShipment.expectedShippingDate,
    draftShipment.pickUpSampleEndTime,
    draftShipment.pickUpSampleStartTime,
    sampleShipment.airwaybill,
    sampleShipment.boxType,
    sampleShipment.carrier,
    sampleShipment.expectedShippingDate,
    sampleShipment.id,
    sampleShipment.numberOfPackages,
    sampleShipment.pickupDatetimeEnd,
    sampleShipment.pickupDatetimeStart,
    sampleShipment.trackingNumber,
  ]);

  const handleFrameChange = (index, field, value) => {
    if (index === 0 && field === "carrier") {
      setCarrierNeedsAttention(false);
    }

    setShipmentDataFrames((currentFrames) => {
      const nextFrames = [...currentFrames];
      nextFrames[index] = { ...(nextFrames[index] || {}) };

      if (field === "startTimeOfShipment" || field === "endTimeOfShipment") {
        const nextValue = timeStringFromValue(value);
        const start =
          field === "startTimeOfShipment"
            ? nextValue
            : timeStringFromValue(nextFrames[index].startTimeOfShipment);
        const startMinutes = minutesFromTimeValue(start);
        const nextMinutes = minutesFromTimeValue(nextValue);

        if (
          field === "endTimeOfShipment" &&
          startMinutes !== null &&
          nextMinutes !== null &&
          nextMinutes < startMinutes
        ) {
          showErrorAlert("End time cannot be before start time.");
          return currentFrames;
        }

        nextFrames[index][field] = nextValue;

        if (field === "startTimeOfShipment") {
          const currentEnd = nextFrames[index].endTimeOfShipment;
          const currentEndMinutes = minutesFromTimeValue(currentEnd);
          if (
            currentEndMinutes === null ||
            (nextMinutes !== null && currentEndMinutes < nextMinutes)
          ) {
            nextFrames[index].endTimeOfShipment = addMinutesToTimeValue(
              nextValue,
              120,
            );
          }
        }
      } else if (field === "boxType") {
        const boxTypes = normalizeBoxTypes(value);
        nextFrames[index].boxType = boxTypes;
        nextFrames[index].numberOfPackages = boxTypes.length
          ? String(boxTypes.length)
          : "";
      } else {
        nextFrames[index][field] = value;
      }

      return nextFrames;
    });
  };

  const buildShipmentDraftPayload = (overrides = {}) => {
    const shipmentFrame = shipmentDataFrames[0];
    if (!shipmentFrame) return null;

    const nextStatus = overrides.status ?? sampleShipment.status ?? "draft";
    const shouldPersistConfirmTracking =
      Boolean(sampleShipment.confirmedDatetime) ||
      Boolean(sampleShipment.confirmedByUser) ||
      nextStatus === "confirmed" ||
      nextStatus === "received";
    const shouldPersistReceiveTracking =
      Boolean(sampleShipment.receiveDatetime) ||
      Boolean(sampleShipment.receiveByUser) ||
      Boolean(sampleShipment.reception?.receiveDatetime) ||
      nextStatus === "received";
    const sampleIds = selectedSamples.map((sample) => sample.id);
    const pickupDatetimeStart = combineDateAndTime(
      shipmentFrame.dateOfShipment,
      shipmentFrame.startTimeOfShipment,
    );
    const pickupDatetimeEnd = combineDateAndTime(
      shipmentFrame.dateOfShipment,
      shipmentFrame.endTimeOfShipment,
    );

    return {
      status: nextStatus,
      centerId: shipmentCenterId,
      pickupAddress: editablePickupAddress,
      destinationAddress: editableDestinationAddress,
      expectedShippingDate: shipmentFrame.dateOfShipment,
      pickupStart: shipmentFrame.startTimeOfShipment,
      pickupEnd: shipmentFrame.endTimeOfShipment,
      pickupDatetimeStart,
      pickupDatetimeEnd,
      carrier: shipmentFrame.carrier,
      airwaybill: shipmentFrame.airwaybill,
      trackingNumber: shipmentFrame.airwaybill,
      numberOfPackages: shipmentFrame.numberOfPackages || null,
      boxType: normalizeBoxTypes(shipmentFrame.boxType).join(", "),
      temperatureProbe,
      sampleIds,
      samples: selectedSamples,
      patients: groupSamplesByPatient(selectedSamples),
      createdByUserId:
        sampleShipment.createdByUserId ||
        draftShipment.createdByUserId ||
        requester?.id ||
        currentUser.id,
      createdAt: requestTrackingDate,
      confirmedDatetime: shouldPersistConfirmTracking
        ? confirmTracking.date || null
        : null,
      receiveDatetime: shouldPersistReceiveTracking
        ? receiveTracking.date || null
        : null,
      requestTracking: {
        name: contactName,
        email: contactEmail,
        date: requestTrackingDate,
      },
      confirmTracking: shouldPersistConfirmTracking
        ? confirmTracking
        : { name: "", email: "", date: "" },
      receiveTracking: shouldPersistReceiveTracking
        ? receiveTracking
        : { name: "", email: "", date: "" },
      ...overrides,
    };
  };

  const sourceDraftSnapshot = buildComparableShipmentDraft({
    centerId: shipmentCenterId,
    pickupAddress: {
      ...pickupAddress,
      city: pickupAddress.city || center?.city || "",
      country: pickupAddress.country || center?.country || "",
      centerNumber:
        pickupAddress.centerNumber ||
        draftShipment.centerNumber ||
        center?.centerNumber ||
        "",
    },
    destinationAddress: autoAppliedDestination || {
      ...destinationAddress,
      hospital: destinationAddress.hospital || destinationText || "",
      city: destinationAddress.city || "",
      country: destinationAddress.country || "",
    },
    pickupDatetimeStart:
      sampleShipment.pickupDatetimeStart ||
      combineDateAndTime(
        draftShipment.expectedShippingDate,
        draftShipment.pickUpSampleStartTime,
      ),
    pickupDatetimeEnd:
      sampleShipment.pickupDatetimeEnd ||
      combineDateAndTime(
        draftShipment.expectedShippingDate,
        draftShipment.pickUpSampleEndTime,
      ),
    carrier: sampleShipment.carrier,
    airwaybill: sampleShipment.airwaybill || sampleShipment.trackingNumber,
    numberOfPackages: sampleShipment.numberOfPackages,
    boxType: sampleShipment.boxType,
    temperatureProbe: sampleShipment.temperatureProbe,
    sampleIds: allSamples.map((sample) => sample.id),
    requestTracking: {
      name: pickupAddress.contactName || "",
      email: pickupAddress.contactEmail || "",
      date: requestDateValue,
    },
    confirmTracking: {
      name: formatTrackingUserName(sampleShipment.confirmedByUser),
      email: sampleShipment.confirmedByUser?.email || "",
      date: sampleShipment.confirmedDatetime || "",
    },
    receiveTracking: {
      name:
        formatTrackingUserName(sampleShipment.receiveByUser) ||
        formatTrackingUserName(sampleShipment.reception?.receiveByUser),
      email:
        sampleShipment.receiveByUser?.email ||
        sampleShipment.reception?.receiveByUser?.email ||
        "",
      date:
        sampleShipment.receiveDatetime ||
        sampleShipment.reception?.receiveDatetime ||
        "",
    },
  });
  const currentDraftSnapshot = buildComparableShipmentDraft(
    buildShipmentDraftPayload(),
  );
  const shipmentSourceKey = JSON.stringify(sourceDraftSnapshot);
  React.useEffect(() => {
    setInitializedShipmentSourceKey(shipmentSourceKey);
  }, [shipmentSourceKey]);
  const hasDraftChanges =
    Boolean(sampleShipment.id) &&
    initializedShipmentSourceKey === shipmentSourceKey &&
    !areShipmentDraftsEqual(currentDraftSnapshot, sourceDraftSnapshot);
  const isRequestedShipment = normalizedShipmentStatus === "requested";
  const isConfirmedShipment = normalizedShipmentStatus === "confirmed";
  const isReceivedShipment = normalizedShipmentStatus === "received";
  const canConfirmShipmentStatus = isRequestedShipment;
  const updateButtonLabel = t("update", "Update");
  const canOpenReception = isConfirmedShipment || isReceivedShipment;
  const confirmationSectionRef = React.useRef(null);
  const scrollToStep = (sectionRef) => {
    sectionRef.current?.focus({ preventScroll: true });
    sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  React.useEffect(() => {
    if (!isRequestedShipmentLoaded) return;
    if (location.hash === "#shipment-confirmation") {
      scrollToStep(confirmationSectionRef);
    }
  }, [location.hash, isRequestedShipmentLoaded]);
  const visibleTrackingSteps = React.useMemo(() => {
    if (isReceivedShipment) return ["request", "confirm", "receive"];
    if (isRequestedShipment || isConfirmedShipment) {
      return ["request", "confirm"];
    }
    return ["request"];
  }, [isConfirmedShipment, isReceivedShipment, isRequestedShipment]);
  // Keep the menu's scope exactly as it was when the shipment was opened.
  const routeContext = {
    ...location.state,
    sampleShipmentId: sampleShipment.id || sampleShipmentId,
  };

  const handleUpdateClick = async ({
    targetStatus = null,
    requireChanges = true,
  } = {}) => {
    if (!canEditShipmentInfo) {
      showErrorAlert(t("notAuthorized", "You are not authorized."));
      return;
    }

    const payload = buildShipmentDraftPayload(
      targetStatus ? { status: targetStatus } : {},
    );
    if (
      !sampleShipment.id ||
      !payload ||
      (requireChanges && !hasDraftChanges)
    ) {
      showErrorAlert("No shipment data");
      return;
    }

    try {
      await store.dispatch(editSampleShipment(sampleShipment.id, payload));
      showSuccessToast(
        targetStatus === "requested"
          ? t("sampleShipmentRequested", "Sample shipment requested")
          : isDraftShipment
            ? t("sampleShipmentDraftSaved", "Sample shipment draft saved")
            : t("sampleShipmentUpdated", "Sample shipment updated"),
      );
    } catch (error) {
      console.error("Error updating sample shipment:", error);
      showErrorAlert("Failed to update the sample shipment. Please try again.");
    }
  };

  const handleDiscardChanges = () => {
    window.location.reload();
  };

  /*
  const handleImportClick = (event, index) => {
    const file = event.target.files[0];
    if (!file) return;

    setShipmentDataFrames((currentFrames) => {
      const nextFrames = [...currentFrames];
      nextFrames[index].selectedFile = file.name;
      return nextFrames;
    });
  };
  */

  const handleConfirmClick = async () => {
    if (!canEditShipmentInfo) {
      showErrorAlert(t("notAuthorized", "You are not authorized."));
      return;
    }

    if (!sampleShipment.id || !canConfirmShipmentStatus) {
      showErrorAlert("No shipment data");
      return;
    }

    try {
      const confirmedDatetime =
        confirmTracking.date || new Date().toISOString();
      const submittedConfirmTracking = {
        name: confirmTracking.name || currentUserConfirmTracking.name,
        email: confirmTracking.email || currentUserConfirmTracking.email,
        date: confirmedDatetime,
      };

      await store.dispatch(
        editSampleShipment(sampleShipment.id, {
          confirmedDatetime,
          confirmedByUserId: currentUser.id,
          status: "confirmed",
        }),
      );
      setConfirmTracking(submittedConfirmTracking);
      showSuccessToast(t("sampleShipmentConfirmed"));
    } catch (error) {
      console.error("Error confirming sample shipment:", error);
      showErrorAlert("Error confirming sample shipment. Please try again.");
    }
  };

  const handleUnconfirmClick = async () => {
    if (!canEditShipmentInfo) {
      showErrorAlert(t("notAuthorized", "You are not authorized."));
      return;
    }

    if (!sampleShipment.id || !isConfirmedShipment) {
      showErrorAlert("No shipment data");
      return;
    }

    const result = await Swal.fire({
      title: t("unconfirmShipment", "Un confirm shipment?"),
      text: t(
        "unconfirmShipmentConfirm",
        "The shipment status will return to requested.",
      ),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("unconfirm", "Un confirm"),
      cancelButtonText: t("cancel", "Cancel"),
      confirmButtonColor: "#1e3179",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      await store.dispatch(
        editSampleShipment(sampleShipment.id, {
          confirmedDatetime: null,
          confirmedByUserId: null,
          status: "requested",
        }),
      );
      showSuccessToast(
        t("sampleShipmentUnconfirmed", "Sample shipment unconfirmed"),
      );
    } catch (error) {
      console.error("Error unconfirming sample shipment:", error);
      showErrorAlert(
        t(
          "sampleShipmentUnconfirmFailed",
          "Error unconfirming sample shipment. Please try again.",
        ),
      );
    }
  };

  const handleReceptionClick = () => {
    const selectedCarrier = normalizeDraftValue(
      shipmentDataFrames[0]?.carrier,
    ).trim();
    const savedCarrier = normalizeDraftValue(sampleShipment.carrier).trim();

    if (!selectedCarrier || selectedCarrier !== savedCarrier) {
      setCarrierNeedsAttention(true);
      showErrorToast(
        t(
          "saveCarrierBeforeReception",
          "Select and save the carrier before opening reception.",
        ),
      );
      carrierFieldRef.current?.scrollIntoView?.({
        behavior: "smooth",
        block: "center",
      });
      carrierSelectRef.current?.focus();
      return;
    }

    const shipmentReference =
      sampleShipment.reference || draftShipment.reference;

    if (!shipmentReference) {
      showErrorAlert("No shipment reference");
      return;
    }

    navigate(
      `/sample-shipments/${encodeURIComponent(shipmentReference)}/reception`,
      { state: routeContext },
    );
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const renderTransportFrame = (frame = {}, index) => (
    <Box
      key={index}
      sx={{
        p: 2,
        borderRadius: 1,
        bgcolor: "grey.50",
        border: "1px solid",
        borderColor: "divider",
        mt: index === 0 ? 0 : 2,
      }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 1.5,
        }}>
        <LocalShippingIcon color="primary" fontSize="small" />
        <Typography sx={{ fontWeight: 700 }}>{t("carrier")}</Typography>
      </Box>
      <Grid container spacing={2} alignItems="stretch">
        {/*
        <Grid item xs={12} md={2}>
          <FormControl fullWidth>
            <InputLabel>{t("samples", "Samples")}</InputLabel>
            <Select
              label={t("samples", "Samples")}
              value="ALL"
              disabled={!canEditShipmentInfo}
              onChange={() => undefined}>
              <MenuItem value="ALL">{t("all", "ALL")}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        */}
        <Grid item xs={12} md={2}>
          <FormControl
            ref={index === 0 ? carrierFieldRef : undefined}
            fullWidth
            error={index === 0 && carrierNeedsAttention}>
            <InputLabel>{t("carrier")}</InputLabel>
            <Select
              inputRef={index === 0 ? carrierSelectRef : undefined}
              label={t("carrier")}
              value={frame.carrier || ""}
              disabled={!canEditShipmentInfo}
              onChange={(event) =>
                handleFrameChange(index, "carrier", event.target.value)
              }>
              <MenuItem value="GoPak">GoPak</MenuItem>
              <MenuItem value="QuickSTAT">QuickSTAT</MenuItem>
              <MenuItem value="Transcourrier">Transcourrier</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label={t("airwaybillNumber")}
            value={frame.airwaybill || ""}
            disabled={!canEditShipmentInfo}
            onChange={(event) =>
              handleFrameChange(index, "airwaybill", event.target.value)
            }
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel id={`box-type-label-${index}`}>
              {t("boxType", "Box Type")}
            </InputLabel>
            <Select
              multiple
              labelId={`box-type-label-${index}`}
              label={t("boxType", "Box Type")}
              value={normalizeBoxTypes(frame.boxType)}
              disabled={!canEditShipmentInfo}
              renderValue={(selected) => selected.join(", ")}
              sx={{
                "& .MuiSelect-select": {
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
              onChange={(event) =>
                handleFrameChange(
                  index,
                  "boxType",
                  normalizeBoxTypes(event.target.value),
                )
              }>
              {BOX_TYPE_OPTIONS.map((boxType) => (
                <MenuItem key={boxType} value={boxType}>
                  <Checkbox
                    size="small"
                    checked={normalizeBoxTypes(frame.boxType).includes(boxType)}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{ "aria-hidden": true }}
                  />
                  <ListItemText primary={boxType} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            type="number"
            label={t("numberOfPackages")}
            value={frame.numberOfPackages || ""}
            disabled={!canEditShipmentInfo}
            onChange={(event) =>
              handleFrameChange(index, "numberOfPackages", event.target.value)
            }
            inputProps={{ min: 0, step: 1 }}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <CheckboxField
            label={
              <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                {t("tempProbe", "Temp probe")}
              </Box>
            }
            checked={temperatureProbe}
            disabled={!canEditShipmentInfo}
            onChange={setTemperatureProbe}
          />
        </Grid>
        {/*
        <Grid item xs={12} md={2}>
          <Button
            fullWidth
            variant="contained"
            component="label"
            startIcon={<CloudUploadIcon />}
            disabled={!canEditShipmentInfo || !airwaybillDocumentActionsEnabled}
            sx={{ minHeight: 56 }}>
            {frame.selectedFile || t("importAirwaybill")}
            <input
              type="file"
              accept=".pdf,.jpeg,.jpg,.png"
              disabled={!airwaybillDocumentActionsEnabled}
              hidden
              onChange={(event) => handleImportClick(event, index)}
            />
          </Button>
        </Grid>
        */}
      </Grid>
    </Box>
  );

  return (
    <Grid item xs={12}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <PageHeader
          title={t("sampleShipmentDetail", "Sample Shipment Detail")}
          downloadLabel={t("confirmation", "Confirmation")}
          iconDownloadLabel="Contents"
          onBack={handleBackClick}
          onIconDownload={() =>
            printSampleShipmentSamples(printableShipment, study)
          }
          onDownload={() =>
            printSampleShipmentDetail(printableShipment, study, {
              includeDestinationAddress: canViewDestinationAddress,
            })
          }
          iconDownloadDisabled={
            !sampleShipment.id || !study || selectedSamples.length === 0
          }
          downloadDisabled={!sampleShipment.id || !study}
        />

        <SampleShipmentSteps
          activeStep={isRequestedShipmentLoaded && !isDraftShipment ? 1 : 0}
          status={isRequestedShipmentLoaded ? normalizedShipmentStatus : ""}
          onConfirm={
            isRequestedShipmentLoaded && !isDraftShipment
              ? () => scrollToStep(confirmationSectionRef)
              : undefined
          }
          onReceive={
            isRequestedShipmentLoaded &&
            canEditShipmentInfo &&
            canOpenReception &&
            !hasDraftChanges
              ? handleReceptionClick
              : undefined
          }
        />

        <Paper variant="outlined" sx={sectionSx}>
          <SectionHeader
            title={t("shipmentInformation", "Shipment Information")}
            description={
              sampleShipment.reference || draftShipment.shipmentNumber
            }
            action={
              <StudyCodeLabelHelper
                label={t("cdlStudyCode", "CDL Study Code")}
                value={cdlStudyCode}
              />
            }
          />
          <Grid container spacing={2}>
            <DetailField
              label={t("shipmentNumber")}
              value={sampleShipment.reference}
            />
            <DetailField
              label={t("status")}
              value={(normalizedShipmentStatus || "draft").replace(
                /^./,
                (status) => status.toUpperCase(),
              )}
            />
            <DetailField
              label={t("center")}
              value={center?.centerNumber || draftShipment.centerNumber}
            />
          </Grid>
        </Paper>

        <Paper variant="outlined" sx={sectionSx}>
          <SectionHeader
            title={t("shippingAddress")}
            description={
              canViewDestinationAddress
                ? "Pickup and destination address"
                : "Pickup address"
            }
          />
          <Grid container spacing={2}>
            <AddressForm
              hospital={hospital}
              setHospital={setHospital}
              ward={ward}
              setWard={setWard}
              building={building}
              setBuilding={setBuilding}
              street={street}
              setStreet={setStreet}
              floor={floor}
              setFloor={setFloor}
              zone={zone}
              setZone={setZone}
              zipcode={zipcode}
              setZipcode={setZipcode}
              phone={contactPhone}
              setPhone={setContactPhone}
              name={contactName}
              setName={setContactName}
              email={contactEmail}
              setEmail={setContactEmail}
              destinationHospital={destinationHospital}
              setDestinationHospital={setDestinationHospital}
              destinationWard={destinationWard}
              setDestinationWard={setDestinationWard}
              destinationBuilding={destinationBuilding}
              setDestinationBuilding={setDestinationBuilding}
              destinationStreet={destinationStreet}
              setDestinationStreet={setDestinationStreet}
              destinationZone={destinationZone}
              setDestinationZone={setDestinationZone}
              destinationFloor={destinationFloor}
              setDestinationFloor={setDestinationFloor}
              destinationZipcode={destinationZipcode}
              setDestinationZipcode={setDestinationZipcode}
              destinationCity={destinationCity}
              destinationCountry={destinationCountry}
              destinationContactName={destinationContactName}
              setDestinationContactName={setDestinationContactName}
              destinationContactPhone={destinationContactPhone}
              setDestinationContactPhone={setDestinationContactPhone}
              destinationContactEmail={destinationContactEmail}
              setDestinationContactEmail={setDestinationContactEmail}
              destinationOptions={destinationOptions}
              selectedDestinationId={selectedDestinationId}
              onDestinationChange={handleDestinationChange}
              fieldErrors={fieldErrors}
              center={center}
              showDestination={canViewDestinationAddress}
              editable={canEditShipmentInfo}
            />
          </Grid>
        </Paper>

        <SampleSelectionSection
          selectedSamples={selectedSamples}
          availableSamples={samples}
          centerId={shipmentCenterId}
          studyId={center?.studyId || study?.id || location.state?.studyId}
          onChange={setSelectedSamples}
          title={t("samples", "Samples")}
          description={`${selectedSamples.length} sample${selectedSamples.length === 1 ? "" : "s"}`}
          sectionSx={sectionSx}
          canEdit={canEditShipmentInfo}
          emptyText={t("noSamplesAvailable", "No samples available.")}
        />

        <Paper variant="outlined" sx={sectionSx}>
          <SectionHeader
            title={t("transportInformation")}
            description={t("pickupExpectedDate", "Pick-up expected date")}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "320px 1fr" },
                gap: 2,
                mb: canEditShipmentInfo ? 2 : 0,
              }}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1,
                  bgcolor: "grey.50",
                  border: "1px solid",
                  borderColor: "divider",
                }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.5,
                  }}>
                  <CalendarMonthIcon color="primary" fontSize="small" />
                  <Typography sx={{ fontWeight: 700 }}>
                    {t("expectedShippingDate")}
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <CustomDatePicker
                    label={t("expectedShippingDate")}
                    name="expectedShippingDate"
                    value={shipmentDataFrames[0]?.dateOfShipment || new Date()}
                    disabled={!canEditShipmentInfo}
                    onChange={(date) =>
                      handleFrameChange(0, "dateOfShipment", date)
                    }
                    minDate={new Date()}
                    gridSize={12}
                  />
                </Grid>
              </Box>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1,
                  bgcolor: "grey.50",
                  border: "1px solid",
                  borderColor: "divider",
                }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    mb: 1.5,
                  }}>
                  <AccessTimeIcon
                    color="primary"
                    fontSize="small"
                    sx={{ mt: 0.25 }}
                  />
                  <Box>
                    <Typography sx={{ fontWeight: 700 }}>
                      {t("pickUpSampleTimeBetween")}
                    </Typography>
                  </Box>
                </Box>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={5}>
                    <TimePicker
                      label={t("startTime")}
                      value={asTimePickerValue(
                        shipmentDataFrames[0]?.startTimeOfShipment,
                      )}
                      ampm={false}
                      disabled={!canEditShipmentInfo}
                      onChange={(time) =>
                        handleFrameChange(0, "startTimeOfShipment", time)
                      }
                      sx={{ width: "100%" }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Typography
                      align="center"
                      color="text.secondary"
                      sx={{ fontWeight: 700 }}>
                      {t("and")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <TimePicker
                      label={t("endTime")}
                      value={asTimePickerValue(
                        shipmentDataFrames[0]?.endTimeOfShipment,
                      )}
                      ampm={false}
                      disabled={!canEditShipmentInfo}
                      onChange={(time) =>
                        handleFrameChange(0, "endTimeOfShipment", time)
                      }
                      minTime={
                        asTimePickerValue(
                          shipmentDataFrames[0]?.startTimeOfShipment,
                        )?.add(1, "hour") || null
                      }
                      sx={{ width: "100%" }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>

            {canViewDestinationAddress && (
              <Box sx={{ mt: 2 }}>
                {renderTransportFrame(shipmentDataFrames[0], 0)}
              </Box>
            )}
          </LocalizationProvider>
        </Paper>

        <Box
          id="shipment-confirmation"
          ref={confirmationSectionRef}
          tabIndex={-1}
          sx={{ scrollMarginTop: 24 }}>
          <SampleShipmentAudit
            sampleShipment={sampleShipment}
            draftShipment={draftShipment}
            fallbackRequester={requester}
            requestDate={requestDate}
            sectionSx={sectionSx}
            visibleSteps={visibleTrackingSteps}
            editable={canEditShipmentInfo}
            trackingValues={{
              request: {
                name: contactName,
                email: contactEmail,
                date: requestTrackingDate,
              },
              confirm: confirmTracking,
              receive: receiveTracking,
            }}
            onTrackingChange={{
              request: handleRequestTrackingChange,
              confirm: setConfirmTracking,
              receive: setReceiveTracking,
            }}
          />
        </Box>

        {canEditShipmentInfo && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
            }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "stretch",
                gap: 1,
                flexDirection: { xs: "column", sm: "row" },
              }}>
              <Tooltip
                title={t(
                  "updateShipmentTooltip",
                  "Save the pending changes to the shipment.",
                )}
                arrow>
                <Box component="span" sx={{ display: "inline-flex" }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => handleUpdateClick()}
                    disabled={!sampleShipment.id || !hasDraftChanges}
                    sx={{ minWidth: 180, minHeight: 48 }}>
                    {updateButtonLabel}
                  </Button>
                </Box>
              </Tooltip>
              {hasDraftChanges && (
                <Tooltip
                  title={t("discardChanges", "Discard changes")}
                  arrow>
                  <Button
                    variant="outlined"
                    startIcon={<CloseIcon />}
                    onClick={handleDiscardChanges}
                    sx={{
                      minHeight: 48,
                      bgcolor: "grey.100",
                      borderColor: "grey.300",
                      color: "text.secondary",
                      "&:hover": {
                        bgcolor: "grey.200",
                        borderColor: "grey.400",
                      },
                    }}>
                    {t("discard", "Discard")}
                  </Button>
                </Tooltip>
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexDirection: { xs: "column", sm: "row" },
              }}>
              {isDraftShipment && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() =>
                    handleUpdateClick({
                      targetStatus: "requested",
                      requireChanges: false,
                    })
                  }
                  disabled={!sampleShipment.id || hasDraftChanges}
                  sx={{ minWidth: 180, minHeight: 48 }}>
                  {t("request", "Request")}
                </Button>
              )}
              {canConfirmShipmentStatus && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleConfirmClick}
                  disabled={!sampleShipment.id || hasDraftChanges}
                  sx={{ minWidth: 180, minHeight: 48 }}>
                  {t("confirm")}
                </Button>
              )}
              {isConfirmedShipment && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleUnconfirmClick}
                  disabled={!sampleShipment.id}
                  sx={{ minWidth: 180, minHeight: 48 }}>
                  {t("unconfirm", "Un confirm")}
                </Button>
              )}
              {canOpenReception && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleReceptionClick}
                  disabled={!sampleShipment.id || hasDraftChanges}
                  sx={{ minWidth: 180, minHeight: 48 }}>
                  {t("reception", "Reception")}
                </Button>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Grid>
  );
};
